import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Shield, Users, GraduationCap, Search, LogOut, RefreshCw, AlertTriangle,
  Mail, Calendar, Lock, Unlock, Trash2, School, ShieldQuestion, Ban,
  UserCheck, ChevronRight, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminPanelAPI, authAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const ROLE_META = {
  admin:   { label: 'Admin',      color: 'bg-rose-100 text-rose-700 border-rose-200',           Icon: Shield },
  teacher: { label: 'Professor',  color: 'bg-indigo-100 text-indigo-700 border-indigo-200',     Icon: GraduationCap },
  student: { label: 'Aluno',      color: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: School },
  '':      { label: 'Sem papel',  color: 'bg-amber-100 text-amber-800 border-amber-300',       Icon: ShieldQuestion },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const Avatar = ({ user, size = 'md' }) => {
  const s = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  if (user.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name}
        className={`${s} rounded-full object-cover border-2 border-white shadow-sm`}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <div className={`${s} rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-outfit font-black flex items-center justify-center shadow-sm`}>
      {(user.name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

const RoleBadge = ({ role }) => {
  const meta = ROLE_META[role] || ROLE_META[''];
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.color}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const StatCard = ({ label, value, tint, Icon, testId }) => (
  <div data-testid={testId} className={`rounded-2xl p-4 border-2 shadow-sm ${tint}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-nunito font-bold uppercase tracking-wide opacity-80">{label}</span>
      <Icon className="w-4 h-4 opacity-80" />
    </div>
    <div className="text-3xl font-outfit font-black mt-1 leading-none">{value}</div>
  </div>
);

// ─── USERS TAB ──────────────────────────────────────────────────────────────
const UsersTab = ({ data, loading, refreshing, reload }) => {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showBlocked, setShowBlocked] = useState('all'); // all | blocked | active
  const [showOrphans, setShowOrphans] = useState(false);
  const [blockDialog, setBlockDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.users || []).filter((u) => {
      if (showOrphans && u.has_classroom) return false;
      if (roleFilter !== 'all') {
        if (roleFilter === 'none') { if (u.role) return false; }
        else if (u.role !== roleFilter) return false;
      }
      if (showBlocked === 'blocked' && !u.is_blocked) return false;
      if (showBlocked === 'active' && u.is_blocked) return false;
      if (!q) return true;
      const hay = `${u.name} ${u.email} ${(u.classrooms || []).map(c => c.name).join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [data.users, search, roleFilter, showBlocked, showOrphans]);

  const exportCsv = () => {
    const rows = [['Nome', 'Email', 'Papel', 'Cadastro', 'Salas', 'Status']];
    filtered.forEach((u) => {
      rows.push([
        u.name, u.email, (ROLE_META[u.role] || ROLE_META['']).label,
        formatDate(u.created_at),
        (u.classrooms || []).map(c => c.name).join(' | ') || 'SEM SALA',
        u.is_blocked ? 'BLOQUEADA' : 'Ativa',
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-usuarios-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`CSV com ${filtered.length} usuário(s) exportado`);
  };

  const doToggleBlock = async () => {
    if (!blockDialog) return;
    setBusyId(blockDialog.user_id);
    try {
      const res = await adminPanelAPI.toggleBlock(blockDialog.user_id, !blockDialog.is_blocked, reason);
      toast.success(res.data.message + (res.data.classrooms_cascaded ? ` · ${res.data.classrooms_cascaded} sala(s) afetada(s)` : ''));
      setBlockDialog(null);
      setReason('');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao alterar status');
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    if (!deleteDialog) return;
    setBusyId(deleteDialog.user_id);
    try {
      const res = await adminPanelAPI.deleteUser(deleteDialog.user_id);
      toast.success(res.data.message);
      setDeleteDialog(null);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao excluir');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total"       value={data.total}                  Icon={Users}          tint="bg-white border-slate-200 text-slate-800"          testId="stat-total" />
        <StatCard label="Admins"      value={data.by_role?.admin || 0}    Icon={Shield}         tint="bg-rose-50 border-rose-200 text-rose-800"          testId="stat-admins" />
        <StatCard label="Professores" value={data.by_role?.teacher || 0}  Icon={GraduationCap}  tint="bg-indigo-50 border-indigo-200 text-indigo-800"    testId="stat-teachers" />
        <StatCard label="Alunos"      value={data.by_role?.student || 0}  Icon={School}         tint="bg-emerald-50 border-emerald-200 text-emerald-800" testId="stat-students" />
        <StatCard label="Bloqueadas"  value={data.blocked || 0}           Icon={Ban}            tint="bg-slate-100 border-slate-300 text-slate-800"      testId="stat-blocked" />
      </div>

      {/* Toolbar */}
      <Card className="p-4 rounded-2xl border-2 border-slate-200">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="admin-users-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, email ou sala…"
              className="pl-9 rounded-xl border-2"
            />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {[
              { key: 'all', label: 'Todos' },
              { key: 'admin', label: 'Admins' },
              { key: 'teacher', label: 'Professores' },
              { key: 'student', label: 'Alunos' },
              { key: 'none', label: 'Sem papel' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setRoleFilter(f.key)}
                data-testid={`admin-role-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold font-nunito border-2 transition
                  ${roleFilter === f.key
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}
              >
                {f.label}
              </button>
            ))}
            <div className="w-px h-6 bg-slate-200 mx-1" />
            {[
              { key: 'all', label: 'Ativos + Bloqueadas' },
              { key: 'active', label: 'Só Ativos' },
              { key: 'blocked', label: 'Só Bloqueadas' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setShowBlocked(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold font-nunito border-2 transition
                  ${showBlocked === f.key
                    ? 'bg-slate-700 text-white border-slate-700'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                  }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={() => setShowOrphans((v) => !v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold font-nunito border-2 transition inline-flex items-center gap-1
                ${showOrphans
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                }`}
            >
              <AlertTriangle className="w-3 h-3" />
              Sem sala
            </button>
          </div>
          <Button
            onClick={exportCsv}
            data-testid="admin-users-export-btn"
            className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
        <div className="mt-3 text-xs text-slate-500 font-nunito">
          Exibindo <span className="font-bold text-slate-800">{filtered.length}</span> de <span className="font-bold text-slate-800">{data.total}</span>
        </div>
      </Card>

      {/* Table */}
      <Card className="rounded-2xl border-2 border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-16 text-center text-slate-500 font-nunito">Carregando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-slate-500 font-nunito">Nenhum usuário com esses filtros.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200 text-slate-600">
                <tr className="text-left">
                  <th className="px-4 py-3 font-nunito font-bold">Usuário</th>
                  <th className="px-4 py-3 font-nunito font-bold">Email</th>
                  <th className="px-4 py-3 font-nunito font-bold">Papel</th>
                  <th className="px-4 py-3 font-nunito font-bold">Cadastro</th>
                  <th className="px-4 py-3 font-nunito font-bold">Sala(s)</th>
                  <th className="px-4 py-3 font-nunito font-bold text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const orphan = !u.has_classroom;
                  const blocked = u.is_blocked;
                  const busy = busyId === u.user_id;
                  return (
                    <tr
                      key={u.user_id}
                      data-testid={`admin-user-row-${u.user_id}`}
                      data-blocked={blocked ? 'true' : 'false'}
                      data-orphan={orphan ? 'true' : 'false'}
                      className={`border-b border-slate-100 last:border-0 transition
                        ${blocked ? 'bg-slate-100/70 opacity-80' : orphan ? 'bg-amber-50/70' : 'hover:bg-slate-50'}
                      `}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar user={u} />
                          <div className="min-w-0">
                            <div className={`font-outfit font-bold ${blocked ? 'text-slate-500 line-through' : 'text-slate-900'} truncate`}>{u.name}</div>
                            {blocked && (
                              <div className="flex items-center gap-1 text-[11px] font-bold text-slate-600 mt-0.5">
                                <Ban className="w-3 h-3" /> Bloqueada · {formatDate(u.blocked_at)}
                              </div>
                            )}
                            {!blocked && orphan && (
                              <div className="flex items-center gap-1 text-[11px] font-bold text-amber-700 mt-0.5">
                                <AlertTriangle className="w-3 h-3" /> Sem sala
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-nunito">
                        <div className="flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-slate-400" />
                          <span className="truncate max-w-[240px]" title={u.email}>{u.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                      <td className="px-4 py-3 text-slate-600 font-nunito text-xs">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-slate-400" />
                          {formatDate(u.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {u.classrooms && u.classrooms.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[260px]">
                            {u.classrooms.map((c) => (
                              <Badge
                                key={c.classroom_id}
                                className={`font-nunito font-bold hover:brightness-105 cursor-default
                                  ${c.is_blocked
                                    ? 'bg-slate-200 text-slate-500 border border-slate-300 line-through'
                                    : 'bg-indigo-100 text-indigo-800 border border-indigo-200'}`}
                              >
                                {c.name}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500 text-white text-xs font-bold shadow-sm">
                            <AlertTriangle className="w-3 h-3" /> SEM SALA
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {blocked ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy}
                              onClick={() => { setBlockDialog(u); setReason(''); }}
                              data-testid={`admin-unblock-${u.user_id}`}
                              className="rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            >
                              <Unlock className="w-3.5 h-3.5 mr-1" /> Desbloquear
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy}
                              onClick={() => { setBlockDialog(u); setReason(''); }}
                              data-testid={`admin-block-${u.user_id}`}
                              className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              <Lock className="w-3.5 h-3.5 mr-1" /> Bloquear
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            onClick={() => setDeleteDialog(u)}
                            data-testid={`admin-delete-${u.user_id}`}
                            className="rounded-lg border-rose-300 text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Block/Unblock Dialog */}
      <Dialog open={!!blockDialog} onOpenChange={(o) => { if (!o) { setBlockDialog(null); setReason(''); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-outfit">
              {blockDialog?.is_blocked ? <><Unlock className="w-5 h-5 text-emerald-600" /> Desbloquear conta</> : <><Lock className="w-5 h-5 text-amber-600" /> Bloquear conta</>}
            </DialogTitle>
            <DialogDescription>
              {blockDialog?.is_blocked
                ? <>A conta de <b>{blockDialog?.name}</b> voltará a ter acesso à plataforma.</>
                : <>Bloquear <b>{blockDialog?.name}</b> encerra sessões ativas e impede login.{(blockDialog?.role || '').toLowerCase() === 'teacher' && <> Todas as salas deste professor também serão bloqueadas.</>}</>
              }
            </DialogDescription>
          </DialogHeader>
          {!blockDialog?.is_blocked && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Motivo (opcional)</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: Uso indevido, aluno duplicado, etc."
                className="rounded-xl"
                rows={3}
                data-testid="admin-block-reason"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBlockDialog(null)}>Cancelar</Button>
            <Button
              onClick={doToggleBlock}
              disabled={busyId === blockDialog?.user_id}
              data-testid="admin-block-confirm"
              className={blockDialog?.is_blocked
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-amber-600 hover:bg-amber-700 text-white'
              }
            >
              {blockDialog?.is_blocked ? 'Desbloquear' : 'Bloquear conta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => { if (!o) setDeleteDialog(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-outfit text-rose-700">
              <Trash2 className="w-5 h-5" /> Excluir permanentemente
            </DialogTitle>
            <DialogDescription>
              Isso apaga a conta de <b>{deleteDialog?.name}</b> ({deleteDialog?.email}) do banco.
              {(deleteDialog?.role || '').toLowerCase() === 'teacher' && <><br/><b>Todas as salas deste professor serão bloqueadas</b> (dados preservados para auditoria).</>}
              {(deleteDialog?.role || '').toLowerCase() === 'student' && <><br/>Registros do aluno serão movidos para inativos.</>}
              <br/>Um backup completo é armazenado antes da exclusão.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog(null)}>Cancelar</Button>
            <Button
              onClick={doDelete}
              disabled={busyId === deleteDialog?.user_id}
              data-testid="admin-delete-confirm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Excluir permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── TEACHERS TAB ───────────────────────────────────────────────────────────
const TeachersTab = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminPanelAPI.listTeachers();
      setTeachers(res.data.teachers || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar professores');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teachers;
    return teachers.filter((t) =>
      `${t.name} ${t.email} ${(t.classrooms || []).map(c => c.name).join(' ')}`.toLowerCase().includes(q)
    );
  }, [teachers, search]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Total"
          value={teachers.length}
          Icon={GraduationCap}
          tint="bg-indigo-50 border-indigo-200 text-indigo-800"
          testId="stat-teachers-total"
        />
        <StatCard
          label="Ativos"
          value={teachers.filter(t => !t.is_blocked).length}
          Icon={UserCheck}
          tint="bg-emerald-50 border-emerald-200 text-emerald-800"
          testId="stat-teachers-active"
        />
        <StatCard
          label="Bloqueados"
          value={teachers.filter(t => t.is_blocked).length}
          Icon={Ban}
          tint="bg-slate-100 border-slate-300 text-slate-800"
          testId="stat-teachers-blocked"
        />
        <StatCard
          label="Salas geridas"
          value={teachers.reduce((sum, t) => sum + (t.classroom_count || 0), 0)}
          Icon={School}
          tint="bg-white border-slate-200 text-slate-800"
          testId="stat-teachers-classrooms"
        />
      </div>

      <Card className="p-4 rounded-2xl border-2 border-slate-200">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome, email ou sala…"
            className="pl-9 rounded-xl border-2"
            data-testid="admin-teachers-search"
          />
        </div>
      </Card>

      {loading ? (
        <Card className="p-16 rounded-2xl text-center text-slate-500 font-nunito">Carregando professores…</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-16 rounded-2xl text-center text-slate-500 font-nunito">Nenhum professor encontrado.</Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filtered.map((t) => (
            <Card
              key={t.user_id}
              data-testid={`admin-teacher-card-${t.user_id}`}
              className={`p-5 rounded-2xl border-2 transition
                ${t.is_blocked ? 'bg-slate-100 border-slate-300 opacity-80' : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-md'}
              `}
            >
              <div className="flex items-start gap-3">
                <Avatar user={t} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-outfit font-bold text-lg ${t.is_blocked ? 'text-slate-500 line-through' : 'text-slate-900'} truncate`}>{t.name}</h3>
                    <RoleBadge role="teacher" />
                    {t.is_blocked && (
                      <Badge className="bg-slate-700 text-white border-slate-700">
                        <Ban className="w-3 h-3 mr-1" /> Bloqueado
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-600 mt-1 font-nunito">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    <span className="truncate" title={t.email}>{t.email}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1 font-nunito">
                    <Calendar className="w-3 h-3" /> Cadastro: {formatDate(t.created_at)}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Salas</div>
                  <div className="font-outfit font-black text-2xl text-slate-900">{t.classroom_count || 0}</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wide">Alunos</div>
                  <div className="font-outfit font-black text-2xl text-slate-900">{t.student_count || 0}</div>
                </div>
              </div>

              {t.classrooms && t.classrooms.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {t.classrooms.map((c) => (
                    <Badge
                      key={c.classroom_id}
                      className={`font-nunito font-bold cursor-default
                        ${c.is_blocked
                          ? 'bg-slate-200 text-slate-500 border border-slate-300 line-through'
                          : 'bg-indigo-100 text-indigo-800 border border-indigo-200'}`}
                    >
                      {c.name}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout: logoutCtx } = useAuth();
  const [tab, setTab] = useState('users');
  const [usersData, setUsersData] = useState({ users: [], total: 0, blocked: 0, without_classroom: 0, by_role: {} });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadUsers = async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await adminPanelAPI.listUsers();
      setUsersData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  };
  useEffect(() => { loadUsers(); }, []);

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch {}
    logoutCtx?.();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950">
      {/* Header */}
      <header className="bg-slate-950/80 backdrop-blur-md border-b-2 border-rose-500/30 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-500 to-red-700 flex items-center justify-center shadow-lg shadow-rose-500/40 ring-2 ring-rose-400/50">
              <Shield className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-outfit font-black text-lg text-white leading-none">Painel do Administrador</h1>
                <span className="px-2 py-0.5 bg-rose-500/20 text-rose-300 text-[10px] uppercase tracking-wider font-black rounded-full border border-rose-400/40">
                  Admin
                </span>
              </div>
              <p className="text-xs text-slate-400 font-nunito mt-1">
                {user?.email || 'Administrador'} · acesso total à plataforma
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => loadUsers(true)}
              disabled={refreshing}
              className="rounded-xl bg-slate-800/50 border-slate-700 text-white hover:bg-slate-700"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="rounded-xl bg-rose-500/20 border-rose-500/40 text-rose-100 hover:bg-rose-500/30"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" /> Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-5">
          <button
            onClick={() => setTab('users')}
            data-testid="admin-tab-users"
            className={`px-4 py-2 rounded-xl font-bold font-nunito text-sm inline-flex items-center gap-2 transition
              ${tab === 'users'
                ? 'bg-white text-slate-900 shadow-lg'
                : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
          >
            <Users className="w-4 h-4" /> Usuários
            <ChevronRight className={`w-3 h-3 transition ${tab === 'users' ? 'rotate-90' : ''}`} />
          </button>
          <button
            onClick={() => setTab('teachers')}
            data-testid="admin-tab-teachers"
            className={`px-4 py-2 rounded-xl font-bold font-nunito text-sm inline-flex items-center gap-2 transition
              ${tab === 'teachers'
                ? 'bg-white text-slate-900 shadow-lg'
                : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
          >
            <GraduationCap className="w-4 h-4" /> Professores
            <ChevronRight className={`w-3 h-3 transition ${tab === 'teachers' ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {/* Body wrapper card so the dark bg contrasts with the table */}
        <div className="bg-slate-50 rounded-3xl p-4 md:p-6 border-2 border-white/10 shadow-2xl">
          {tab === 'users' ? (
            <UsersTab data={usersData} loading={loading} refreshing={refreshing} reload={loadUsers} />
          ) : (
            <TeachersTab />
          )}
        </div>
      </div>
    </div>
  );
}
